export function formatNumberLong(input: number, precision?: number): string {
  if (input === null) {
    return '0.0';
  }
  const formattedNumber = input.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: precision ?? (input < 1000 ? 1 : 0),
  });
  return formattedNumber;
}

export function getCamelCase(str: string): string {
  // First apply the original camel case logic
  const camelCased = str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('-')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('_');

  // Then apply the capitalize after numbers logic
  return getCamelCaseAfterNumbers(camelCased);
}

export function getSnakeCase(str: string): string {
  return str
    .split(' ')
    .map((word) => word.toLowerCase())
    .join('_');
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return `${date.toLocaleString('en-US', {
    month: 'short',
  })} ${date.toLocaleString('en-US', {
    day: 'numeric',
  })}, ${date.getFullYear()}`;
}

export function isProjectNameValid(projectName: string): boolean {
  // For single character project names
  if (projectName.length === 1) {
    return /^[a-zA-Z0-9]$/.test(projectName);
  }

  // For multi-character project names
  const projectNamePattern =
    /^(?=.{1,255}$)(?!.*[_.-]{2})(?!.*\s{2})[a-zA-Z0-9](?!.*(\.git|\.atom)$)[a-zA-Z0-9._\s-]*[a-zA-Z0-9]$/;
  return projectNamePattern.test(projectName);
}

/**
 * Capitalizes letters that come after numbers in a string
 * @param str The input string
 * @returns The string with letters after numbers capitalized
 */
export function getCamelCaseAfterNumbers(str: string): string {
  if (str === '') {
    return '';
  }

  let result = '';
  for (let i = 0; i < str.length; i++) {
    const currentChar = str[i];
    const nextChar = i < str.length - 1 ? str[i + 1] : '';

    // Add the current character to the result
    result += currentChar;

    // If current character is a number and next character is a letter
    if (/\d/.test(currentChar) && /[a-zA-Z]/.test(nextChar)) {
      // Skip the next character (we'll add it capitalized)
      i++;
      // Add the capitalized next character
      result += nextChar.toUpperCase();
    }
  }

  return result;
}

/**
 * Formats a date into a human-readable relative time string
 * Handles timestamps and converts them to the user's local timezone for accurate relative time calculation
 * @param input An ISO date string (preferably with timezone info) or Date object
 * @returns A relative time string like "2 hours ago", "just now", etc.
 */
export function formatRelativeTime(input: string | Date | null | undefined): string | null {
  if (input === null || input === undefined) {
    return null;
  }

  let date: Date;

  try {
    // Parse the input directly - Date constructor handles both space-separated and T-separated formats
    date = typeof input === 'string' ? new Date(input) : input;

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date provided to formatRelativeTime:', input);
      return null;
    }
  } catch (error) {
    console.warn('Error parsing date in formatRelativeTime:', input, error);
    return null;
  }

  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30.44); // Average days per month
  const diffInYears = Math.floor(diffInDays / 365.25); // Account for leap years

  // Less than a minute
  if (diffInSeconds < 60) {
    return 'just now';
  }

  // Less than an hour
  if (diffInMinutes < 60) {
    return diffInMinutes === 1 ? '1 minute ago' : `${diffInMinutes} minutes ago`;
  }

  // Less than a day
  if (diffInHours < 24) {
    return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
  }

  // Less than a week
  if (diffInDays < 7) {
    return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
  }

  // Less than a month
  if (diffInWeeks < 4) {
    return diffInWeeks === 1 ? '1 week ago' : `${diffInWeeks} weeks ago`;
  }

  // Less than a year
  if (diffInMonths < 12) {
    return diffInMonths === 1 ? '1 month ago' : `${diffInMonths} months ago`;
  }

  // A year or more
  return diffInYears === 1 ? '1 year ago' : `${diffInYears} years ago`;
}
